import { fontById, installFontsStylesheet, loadFont, type EngraveFont } from './fonts';
import { inkById, inkLowContrast, type Ink, type InkId } from './inks';
import {
  CAP_STEPS_MM, capStep, cleanText, cloneState, customSlots, customTextCount, DEFAULT_INNER_RIGHT, defaultEngraveState, defaultStyle,
  EDITABLE_SLOTS, engravePayload, fullPayload, isCustom, isEditable, LOCKED_SLOT, NUDGE_MM, ROT_MAX, roundPlace, sameState, slotFace, slotSide, SLOTS, styleOf, tooLong,
  type EditableSlot, type EngravePayload, type EngraveState, type FreePlace, type SlotKey, type TempleFace,
} from './state';
import { canvasMeasure, logoPlace, placeOf, sharedMeasureContext, textFit, type TextFit } from './layout';
import { templeScene, type PlacementIssue, type Scene } from './scene';
import { templeImage, type TempleImage } from './templeImage';
import { TEMPLE_INFO } from './zones';

/**
 * A110Z — the engraving of the session: state (kept across models), the model whose temples are
 * shown, the acetate chosen in Couleurs, the slot being edited and the face shown, plus the
 * temple image loading. The desktop panel, the desktop band, the mobile screen and the order
 * panel all read it and subscribe to `onChange`.
 */
export interface EngraveFrame {
  id: string;
  model: string;
  colorway: string;
  /** Frame's own acetate colour (for the « Peu visible » check on « Original »). */
  acetateColor: string | null;
}

export interface EngraveSummary {
  font: EngraveFont;
  /** Ink of the inside right text (A110Z: the one ink); each view's own ink is in `inks` (A201). */
  ink: Ink;
  /** A201: the ink of each view, the logo's included. */
  inks: Record<SlotKey, Ink>;
  /** Number of customer texts. */
  texts: number;
  payload: EngravePayload;
  /**
   * A110Z3: a text placed by hand, for the workshop — its centre in mm from the hinge along the temple, its
   * offset across it from the middle line (mm, + = lower) and its tilt (degrees, + = clockwise as shown).
   */
  placesMm?: Partial<Record<SlotKey, FreePlace>>;
  /**
   * A110Z2: capital height engraved for each text (mm, 0.1 mm), as drawn on the current model — the
   * chosen step, or less when the temple is too thin or the text too long for its zone.
   */
  capsMm?: Partial<Record<SlotKey, number>>;
  /** A201: the views placed by hand (the others are where the automatic placement puts them). */
  movedByHand?: SlotKey[];
}

export type EngraveEvent = 'state' | 'frame' | 'image' | 'focus' | 'face' | 'warn';

/** A201 quick positions along the engraving zone. */
export type QuickPlace = 'hinge' | 'middle' | 'ear';
export const QUICK_PLACES: readonly QuickPlace[] = ['hinge', 'middle', 'ear'];

export class EngraveSession {
  private _state: EngraveState = defaultEngraveState();
  private _frame: EngraveFrame = { id: '', model: '', colorway: '', acetateColor: null };
  private _acetate = 'original';
  private _acetateColor: string | null = null;
  private _active: SlotKey | null = null;
  private _face: TempleFace = 'int';
  private _image: TempleImage | null = null;
  private imageToken = 0;
  /** Per-slot transient messages (« Caractère non gravable retiré »), cleared on the next edit. */
  private _warn: Partial<Record<EditableSlot, string>> = {};
  private listeners: Array<(event: EngraveEvent) => void> = [];
  private fontsReady = new Set<string>();
  private fontsPending = new Set<string>();
  /** A201 « Annuler » : the states before the last changes (most recent last). */
  private history: EngraveState[] = [];
  private static readonly HISTORY_MAX = 40;
  /** Typing and dragging (same control, quick succession) make one « Annuler » step. */
  private lastKey = '';
  private lastAt = 0;
  private static readonly MERGE_MS = 900;

  get state(): EngraveState { return this._state; }
  get frame(): EngraveFrame { return this._frame; }
  get acetate(): string { return this._acetate; }
  get active(): SlotKey | null { return this._active; }
  get face(): TempleFace { return this._face; }
  get image(): TempleImage | null { return this._image; }
  get font(): EngraveFont { return fontById(this._state.font); }
  /** Ink of the view being edited (the inside right one when none is). */
  get ink(): Ink { return inkById(this._state.inks[this._active ?? 'ir']); }
  /** A201: the ink of a view. */
  inkOf(slot: SlotKey): Ink { return inkById(this._state.inks[slot]); }
  /** A201: « Annuler » is possible. */
  get canUndo(): boolean { return this.history.length > 0; }
  get warnings(): Readonly<Partial<Record<EditableSlot, string>>> { return this._warn; }
  /** The model has a temple photo (otherwise the preview shows the zone on a placeholder). */
  get hasTemple(): boolean { return Boolean(TEMPLE_INFO[this._frame.id]); }

  onChange(listener: (event: EngraveEvent) => void): () => void {
    this.listeners.push(listener);
    return () => { this.listeners = this.listeners.filter(l => l !== listener); };
  }

  private emit(event: EngraveEvent): void {
    for (const l of this.listeners) l(event);
  }

  // ------------------------------------------------------------ model / acetate

  /** New model on the face: the temples change, the engraving stays. Returns true when something custom was kept. */
  setFrame(frame: EngraveFrame): boolean {
    const changed = frame.id !== this._frame.id;
    this._frame = frame;
    if (changed) this.loadImage();
    this.emit('frame');
    return changed && isCustom(this._state);
  }

  /** Acetate chosen in Couleurs (`original` or a library id) and its colour for the contrast check. */
  setAcetate(acetate: string, color: string | null): void {
    if (acetate === this._acetate && color === this._acetateColor) return;
    this._acetate = acetate;
    this._acetateColor = color;
    this.loadImage();
  }

  /** Acetate colour the ink is seen against. */
  get surfaceColor(): string | null {
    return this._acetate === 'original' ? this._frame.acetateColor : this._acetateColor;
  }

  lowContrast(ink: Ink = this.ink): boolean {
    return inkLowContrast(ink, this.surfaceColor);
  }

  private loadImage(): void {
    const token = ++this.imageToken;
    if (!this._frame.id) { this._image = null; this.emit('image'); return; }
    void templeImage(this._frame.id, this._acetate).then(img => {
      if (token !== this.imageToken) return;
      this._image = img;
      this.emit('image');
    });
  }

  // ------------------------------------------------------------ fonts

  /** Fetches the stylesheet and the current font; `onChange('state')` fires once a font becomes usable. */
  prepareFonts(): void {
    installFontsStylesheet();
    this.ensureFont(this.font);
  }

  ensureFont(font: EngraveFont): void {
    if (this.fontsReady.has(font.id) || this.fontsPending.has(font.id)) return;
    this.fontsPending.add(font.id);
    void loadFont(font).then(() => {
      this.fontsPending.delete(font.id);
      this.fontsReady.add(font.id);
      this.emit('state');
    });
  }

  // ------------------------------------------------------------ edits

  /** A201: every change goes through here, so that « Annuler » can bring the previous state back. */
  private commit(next: EngraveState, key = ''): void {
    if (sameState(next, this._state)) return;
    const now = Date.now();
    // Typing in one field is one step however slowly it is typed (until another change or another field);
    // a drag or a slider, while it keeps moving.
    const typing = key.startsWith('text:');
    const merge = key !== '' && key === this.lastKey && (typing || now - this.lastAt < EngraveSession.MERGE_MS) && this.history.length > 0;
    if (!merge) {
      this.history.push(this._state);
      if (this.history.length > EngraveSession.HISTORY_MAX) this.history.shift();
    }
    this.lastKey = key;
    this.lastAt = now;
    this._state = next;
    this.emit('state');
  }

  /** A201 « Annuler » : the last change undone (text, colour, size, place, tilt, font, copy, reset). */
  undo(): void {
    const previous = this.history.pop();
    if (!previous) return;
    this.lastKey = '';
    this._state = previous;
    this._warn = {};
    this.emit('state');
  }

  setText(slot: EditableSlot, raw: string): void {
    const { text, stripped } = cleanText(raw);
    if (text === this._state.texts[slot] && !stripped) return;
    const next = cloneState(this._state);
    next.texts[slot] = text;
    this._warn = { ...this._warn, [slot]: stripped ? 'stripped' : undefined };
    if (sameState(next, this._state)) this.emit('state'); else this.commit(next, `text:${slot}`);
    if (stripped) this.emit('warn');
  }

  /** Puts « Handcrafted in Bali » back on the inner right temple. */
  restoreInnerRight(): void {
    const next = cloneState(this._state);
    next.texts.ir = DEFAULT_INNER_RIGHT;
    next.style.ir = defaultStyle();
    this._warn = { ...this._warn, ir: undefined };
    this.commit(next);
  }

  setFont(id: string): void {
    if (id === this._state.font) return;
    const next = cloneState(this._state);
    next.font = id;
    this.ensureFont(fontById(id));
    this.commit(next);
  }

  /** A201: the ink of one view (the one being edited by default), or of every view with `'all'`. */
  setInk(id: InkId, slot: SlotKey | 'all' = this._active ?? 'ir'): void {
    const next = cloneState(this._state);
    for (const s of slot === 'all' ? SLOTS : [slot]) next.inks[s] = id;
    this.commit(next);
  }

  /** Letter height of one line, or the logo's height (mm, snapped to the slider's steps). */
  setCap(slot: SlotKey, mm: number): void {
    const cap = CAP_STEPS_MM[capStep(mm)]!;
    if (Math.abs(styleOf(this._state, slot).cap - cap) < 1e-6) return;
    const next = cloneState(this._state);
    styleOf(next, slot).cap = cap;
    this.commit(next);
  }

  /** « Même hauteur partout »: every written line takes this height. */
  setCapAll(mm: number): void {
    const cap = CAP_STEPS_MM[capStep(mm)]!;
    const slots = EDITABLE_SLOTS.filter(s => this._state.texts[s] !== '' && Math.abs(this._state.style[s].cap - cap) > 1e-6);
    if (!slots.length) return;
    const next = cloneState(this._state);
    for (const s of slots) next.style[s].cap = cap;
    this.commit(next);
  }

  /** Written lines whose height differs from this slot's (for the « même hauteur partout » link). */
  otherHeights(slot: EditableSlot): EditableSlot[] {
    const cap = this._state.style[slot].cap;
    return EDITABLE_SLOTS.filter(s => s !== slot && this._state.texts[s] !== '' && Math.abs(this._state.style[s].cap - cap) > 1e-6);
  }

  // ------------------------------------------------------------ placement (A110Z3)

  /** The geometry of the view that shows this slot, on the current model. */
  scene(slot: SlotKey): Scene {
    return templeScene({ templeId: this._frame.id, side: slotSide(slot), face: slotFace(slot), image: this._image, state: this._state });
  }

  /** The view's placement as a free one (the automatic placement converted when it was never moved). */
  freePlace(slot: SlotKey): FreePlace | null {
    const own = styleOf(this._state, slot).place;
    if (own) return own;
    const sc = this.scene(slot);
    if (sc.text) return roundPlace(placeOf(sc.box, sc.text));
    if (sc.logo) return roundPlace(logoPlace(sc.box, sc.logo));
    return null;
  }

  /**
   * A text or the logo placed by hand (null: back to the automatic placement). A201 (« le texte reste sur la
   * branche ») : with `clamp`, a move that would take it off the temple or onto the hinge stops at the edge.
   */
  setPlace(slot: SlotKey, place: FreePlace | null, opts: { clamp?: boolean } = {}): void {
    let p = place ? roundPlace(place) : null;
    if (p && opts.clamp) p = this.clampPlace(slot, p);
    const cur = styleOf(this._state, slot).place;
    if ((!p && !cur) || (p && cur && p.x === cur.x && p.y === cur.y && p.rot === cur.rot)) return;
    const next = cloneState(this._state);
    styleOf(next, slot).place = p;
    this.commit(next, `place:${slot}`);
  }

  /** A201: what would go wrong with the view at this placement (off the temple, on the hinge), on this model. */
  placeIssue(slot: SlotKey, place: FreePlace): PlacementIssue | null {
    return this.sceneWith(slot, place).issue;
  }

  /**
   * A201: from where the view is now, as far towards `to` as it stays on the temple and off the hinge. A view
   * already off (made larger, another model) moves freely so that the customer can bring it back.
   */
  clampPlace(slot: SlotKey, to: FreePlace): FreePlace {
    if (!this.placeIssue(slot, to)) return to;
    const from = this.freePlace(slot);
    if (!from || this.placeIssue(slot, roundPlace(from))) return to;
    const at = (k: number): FreePlace => roundPlace({ x: from.x + (to.x - from.x) * k, y: from.y + (to.y - from.y) * k, rot: from.rot + (to.rot - from.rot) * k });
    let lo = 0, hi = 1;
    let best = roundPlace(from);
    for (let i = 0; i < 12; i++) {
      const mid = (lo + hi) / 2;
      const cand = at(mid);
      if (this.placeIssue(slot, cand)) hi = mid; else { lo = mid; best = cand; }
    }
    return best;
  }

  /**
   * A201 « Même chose sur l'autre branche » : the outside text, its height, ink and placement copied to the
   * other temple's outside, mirrored (the same distance from the hinge, the tilt reversed as seen).
   */
  copyToOther(slot: EditableSlot): EditableSlot | null {
    const other: EditableSlot | null = slot === 'el' ? 'er' : slot === 'er' ? 'el' : null;
    if (!other) return null;
    const next = cloneState(this._state);
    next.texts[other] = next.texts[slot];
    next.inks[other] = next.inks[slot];
    const src = next.style[slot];
    next.style[other] = { cap: src.cap, place: src.place ? { x: src.place.x, y: src.place.y, rot: -src.place.rot } : null };
    this.commit(next);
    return other;
  }

  /**
   * A201 quick positions: « Près de la charnière », « Milieu », « Vers l'oreille » — the text's centre at the
   * start, the middle or the end of the engraving zone, level, on the zone's middle line.
   */
  quickPlace(slot: SlotKey, where: QuickPlace): void {
    const target = this.quickTarget(slot, where);
    if (target) this.setPlace(slot, target);
  }

  /** Where a quick position puts the view (null when there is nothing to place). */
  quickTarget(slot: SlotKey, where: QuickPlace): FreePlace | null {
    // Two passes: placed by hand, a text keeps its exact height (the automatic placement may have reduced it).
    let place: FreePlace | null = null;
    for (let pass = 0; pass < 2; pass++) {
      const sc = place ? this.sceneWith(slot, place) : this.scene(slot);
      const width = sc.text ? sc.text.width : sc.logo ? sc.logo.width : 0;
      if (!width) return null;
      const lenMm = sc.box.len * sc.box.mmPerPx;
      const halfMm = (width * sc.box.mmPerPx) / 2;
      const pad = lenMm * 0.05;
      const start = sc.box.zoneStartMm + pad + halfMm;
      const end = sc.box.zoneStartMm + lenMm - pad - halfMm;
      const x = where === 'hinge' ? start : where === 'ear' ? Math.max(start, end) : sc.box.zoneStartMm + lenMm / 2;
      place = roundPlace({ x, y: 0, rot: 0 });
    }
    return place;
  }

  /** The view's geometry with another placement (nothing changes). */
  private sceneWith(slot: SlotKey, place: FreePlace): Scene {
    const state = cloneState(this._state);
    styleOf(state, slot).place = place;
    return templeScene({ templeId: this._frame.id, side: slotSide(slot), face: slotFace(slot), image: this._image, state });
  }

  /** The quick position the view is at, if any (its chip shows pressed). */
  quickAt(slot: SlotKey): QuickPlace | null {
    const p = styleOf(this._state, slot).place;
    if (!p) return null;
    for (const where of QUICK_PLACES) {
      const t = this.quickTarget(slot, where);
      if (t && Math.abs(t.x - p.x) < 0.11 && Math.abs(t.y - p.y) < 0.11 && Math.abs(t.rot - p.rot) < 0.6) return where;
    }
    return null;
  }

  /** Arrow buttons: one step (NUDGE_MM) left/right/up/down as seen on the card. */
  nudge(slot: SlotKey, dx: -1 | 0 | 1, dy: -1 | 0 | 1): void {
    const p = this.freePlace(slot);
    if (!p) return;
    // On screen, the tip is to the right when the hinge is on the left (mirrored view), to the left otherwise.
    const tipRight = this.scene(slot).box.hingeLeft;
    this.setPlace(slot, { x: p.x + dx * NUDGE_MM * (tipRight ? 1 : -1), y: p.y + dy * NUDGE_MM, rot: p.rot }, { clamp: true });
  }

  /** Tilt from the temple's axis (degrees, clockwise as shown). */
  setRot(slot: SlotKey, deg: number): void {
    const p = this.freePlace(slot);
    if (!p) return;
    this.setPlace(slot, { ...p, rot: Math.max(-ROT_MAX, Math.min(ROT_MAX, deg)) }, { clamp: true });
  }

  /** « Recentrer » : back to the automatic placement (the height is kept). */
  resetPlace(slot: SlotKey): void {
    this.setPlace(slot, null);
  }

  /** A text (or the logo) placed by hand that leaves the temple or touches the hinge, on the current model. */
  issue(slot: SlotKey): PlacementIssue | null {
    if (!styleOf(this._state, slot).place) return null;
    if (isEditable(slot) && !this._state.texts[slot]) return null;
    return this.scene(slot).issue;
  }

  /** Back to the house engraving: texts, font, colours, sizes and positions (A201: « Annuler » brings it back). */
  reset(): void {
    this._warn = {};
    this.commit(defaultEngraveState());
  }

  // ------------------------------------------------------------ focus / face

  /** Slot being edited; selecting a slot shows its face. */
  setActive(slot: SlotKey | null): void {
    if (slot && slotFace(slot) !== this._face) { this._face = slotFace(slot); this.emit('face'); }
    if (slot === this._active) return;
    // Another field: what is typed there next is a new « Annuler » step.
    this.lastKey = '';
    this._active = slot;
    this.emit('focus');
  }

  setFace(face: TempleFace): void {
    if (face === this._face) return;
    this._face = face;
    this.emit('face');
  }

  // ------------------------------------------------------------ reads

  isCustom(): boolean { return isCustom(this._state); }
  tooLong(slot: EditableSlot): boolean { return tooLong(this._state.texts[slot]); }
  isLocked(slot: SlotKey): boolean { return slot === LOCKED_SLOT || !isEditable(slot); }
  customSlots(): EditableSlot[] { return customSlots(this._state); }

  /** How the slot's text fits its zone on this model (what the size and position controls can still change). */
  fit(slot: EditableSlot): TextFit | null {
    // A201: the view's own zone and room (a long text runs on along the temple before it is reduced).
    const sc = this.scene(slot);
    return textFit(sc.box, sc.font, this._state.texts[slot], canvasMeasure(sharedMeasureContext(), sc.font), sc.room);
  }

  /**
   * For « Je la veux »: null while nothing differs from the house engraving. A201 (David : « il me faut toutes
   * les infos des textes ») : every view carries its height, its ink and where it is — placed by hand or not —
   * as millimetres from the hinge, offset across the temple and tilt; the logo too.
   */
  summary(): EngraveSummary | null {
    return engravePayload(this._state) ? this.facts() : null;
  }

  /** A201: every detail of the engraving as it is now, the house one included (the workshop sheet). */
  facts(): EngraveSummary {
    const payload = fullPayload(this._state);
    const info = TEMPLE_INFO[this._frame.id];
    const placesMm: Partial<Record<SlotKey, FreePlace>> = {};
    const capsMm: Partial<Record<SlotKey, number>> = {};
    const movedByHand: SlotKey[] = [];
    const where = (p: FreePlace): FreePlace => ({ x: Math.round(p.x * 2) / 2, y: Math.round(p.y * 2) / 2, rot: Math.round(p.rot) });
    for (const p of payload.slots) {
      if (!p.text) continue;
      if (p.place) {
        // Placed by hand: exactly the chosen height, where the customer put it.
        capsMm[p.slot] = p.cap;
        placesMm[p.slot] = where(p.place);
        movedByHand.push(p.slot);
        continue;
      }
      const fit = this.fit(p.slot);
      const step = fit?.steps[capStep(p.cap)];
      capsMm[p.slot] = Math.round((info && step ? Math.min(p.cap, step.capMm) : p.cap) * 10) / 10;
      const auto = info ? this.freePlace(p.slot) : null;
      if (auto) placesMm[p.slot] = where(auto);
    }
    capsMm.il = this.scene('il').text?.capMm ?? payload.logo.cap;
    const logo = payload.logo.place ?? (info ? this.freePlace(LOCKED_SLOT) : null);
    if (logo) placesMm.il = where(logo);
    if (payload.logo.place) movedByHand.push(LOCKED_SLOT);
    const inks = Object.fromEntries(SLOTS.map(s => [s, inkById(this._state.inks[s])])) as Record<SlotKey, Ink>;
    return { font: this.font, ink: inks.ir, inks, texts: customTextCount(this._state), payload, placesMm, capsMm, movedByHand };
  }

  /** Every editable slot, for the desktop list. */
  get editableSlots(): readonly EditableSlot[] { return EDITABLE_SLOTS; }
}
